import { useMemo, useState } from 'react'

import { DOW, buildCells, gridStartOf, localDateStr, parseLocalDate } from '../lib/calendar.js'
import { fetchKrHolidays, type HolidayMap } from '../lib/holidays.js'
import { useAsync } from '../lib/useAsync.js'
import { BottomSheet } from './BottomSheet.js'
import s from './DatePickerSheet.module.css'

/**
 * 날짜 고르기 바텀시트(주문서 희망 배송일, 회의 S4). 월 달력 + 공휴일 표시(Nager.Date, 실패하면 표시만 빠짐).
 * min 이전 날짜는 고를 수 없다. 날짜를 누르면 바로 고르고 닫힌다(확인 버튼 없음 — 한 탭).
 */
export function DatePickerSheet({
  title,
  value,
  min,
  onPick,
  onClose,
}: {
  title: string
  /** 현재 값(YYYY-MM-DD). 비었으면 min의 달부터 보여준다. */
  value: string
  /** 고를 수 있는 가장 이른 날(YYYY-MM-DD, 포함). */
  min: string
  onPick: (date: string) => void
  onClose: () => void
}) {
  const minMonth = useMemo(() => {
    const d = parseLocalDate(min)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  }, [min])
  const [month, setMonth] = useState(() => {
    const d = parseLocalDate(value && value >= min ? value : min)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  // 그 달 날짜가 하나도 없는 끝 주는 빈 줄만 남겨 마지막 주가 붕 떠 보인다 — 필요한 주까지만 그린다.
  const cells = useMemo(() => {
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const weeks = Math.ceil((month.getDay() + days) / 7)
    return buildCells(gridStartOf(month)).slice(0, weeks * 7)
  }, [month])
  const holidays = useAsync<HolidayMap>(
    () => fetchKrHolidays([...new Set(cells.map((c) => c.getFullYear()))]),
    [month.getFullYear(), month.getMonth()],
  )
  const holidayMap = holidays.data ?? {}
  const canPrev = month > minMonth
  const shift = (n: number) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1))
  const today = localDateStr(new Date())

  return (
    <BottomSheet title={title} onClose={onClose}>
      <div className={s.head}>
        <button type="button" className={s.nav} onClick={() => shift(-1)} disabled={!canPrev} aria-label="이전 달">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m15 6-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className={s.month} aria-live="polite">
          {month.getFullYear()}년 {month.getMonth() + 1}월
        </span>
        <button type="button" className={s.nav} onClick={() => shift(1)} aria-label="다음 달">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div className={s.grid} role="group" aria-label={`${month.getFullYear()}년 ${month.getMonth() + 1}월`}>
        {DOW.map((d, i) => (
          <span key={d} className={s.dow} data-dow={i}>
            {d}
          </span>
        ))}
        {cells.map((c) => {
          const iso = localDateStr(c)
          const inMonth = c.getMonth() === month.getMonth()
          const holiday = holidayMap[iso]
          const past = iso < min
          const on = iso === value
          if (!inMonth) return <span key={iso} className={s.pad} aria-hidden="true" />
          return (
            <button
              key={iso}
              type="button"
              className={s.day}
              data-dow={c.getDay()}
              data-holiday={holiday ? 'true' : undefined}
              data-today={iso === today ? 'true' : undefined}
              aria-pressed={on}
              aria-label={`${c.getMonth() + 1}월 ${c.getDate()}일 (${DOW[c.getDay()]})${holiday ? ` ${holiday}` : ''}${iso === today ? ' 오늘' : ''}`}
              title={holiday}
              disabled={past}
              onClick={() => onPick(iso)}
            >
              <span className={s.num}>{c.getDate()}</span>
              {iso === today ? (
                <span className={s.todayTag} aria-hidden="true">
                  오늘
                </span>
              ) : holiday ? (
                <span className={s.dot} aria-hidden="true" />
              ) : null}
            </button>
          )
        })}
      </div>
      <p className={s.legend}>
        <span className={s.dotInline} aria-hidden="true" /> 공휴일
      </p>
    </BottomSheet>
  )
}
